import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const QRInfo = () => {
  const [activeTab, setActiveTab] = useState('comparison');
  
  // URL del dominio de producción para el QR
  const productionUrl = 'https://gpt-jdp.com/qr-info';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(productionUrl)}&bgcolor=ffffff&color=000000`;

  // Tabla comparativa estratégica de mercado
  const marketComparison = [
    {
      feature: "Generación de contenido",
      gptjdp: true,
      gptjdpDetail: "Multi-modelo (GPT-5.2, Claude 4, Gemini)",
      competitors: "ChatGPT, Claude, Jasper",
      competitorDetail: "Un modelo por plataforma",
      advantage: "4+ modelos en una sola plataforma"
    },
    {
      feature: "Automatización de tareas",
      gptjdp: true,
      gptjdpDetail: "Chatbot + Workflows integrados",
      competitors: "Zapier, Make, n8n",
      competitorDetail: "Solo automatización sin IA",
      advantage: "IA + Automatización unificado"
    },
    {
      feature: "Sistema Multi-agentes",
      gptjdp: true,
      gptjdpDetail: "Agentes coordinados (Jarvis próximamente)",
      competitors: "OpenAI Agents, AutoGPT",
      competitorDetail: "Experimental/limitado",
      advantage: "Agentes empresariales controlados"
    },
    {
      feature: "Seguridad y control IA",
      gptjdp: true,
      gptjdpDetail: "AEGIS, Centinel, Nexus Code Guard",
      competitors: "Ninguno integrado",
      competitorDetail: "Sin protocolos de seguridad IA",
      advantage: "Protocolos de seguridad propios"
    },
    {
      feature: "Marketing & RRSS",
      gptjdp: true,
      gptjdpDetail: "Contenido + Imágenes + Programación",
      competitors: "Jasper, Copy.ai",
      competitorDetail: "Solo texto",
      advantage: "Solución integral de marketing"
    },
    {
      feature: "Herramienta interna invisible",
      gptjdp: true,
      gptjdpDetail: "IA que opera sin supervisión constante",
      competitors: "Ninguno",
      competitorDetail: "Requiere input manual",
      advantage: "Automatización real empresarial"
    },
    {
      feature: "Gobernanza IA",
      gptjdp: true,
      gptjdpDetail: "RED CORE Protocol",
      competitors: "Ninguno",
      competitorDetail: "Sin gobernanza estructurada",
      advantage: "Control humano absoluto"
    },
    {
      feature: "Auto-mejora controlada",
      gptjdp: true,
      gptjdpDetail: "Feedback loops con límites",
      competitors: "Ninguno",
      competitorDetail: "Sin auto-mejora segura",
      advantage: "Evolución controlada del sistema"
    },
    {
      feature: "Enfoque empresa",
      gptjdp: true,
      gptjdpDetail: "B2B + Operaciones internas",
      competitors: "Parcial (ChatGPT Teams)",
      competitorDetail: "Enfoque B2C principalmente",
      advantage: "Diseñado para empresas"
    },
    {
      feature: "Compliance futuro",
      gptjdp: true,
      gptjdpDetail: "Preparado para regulaciones IA",
      competitors: "No preparado",
      competitorDetail: "Sin consideración regulatoria",
      advantage: "Ready para normativas UE/Chile"
    }
  ];

  // Ahorro estimado
  const savingsComparison = [
    {
      service: "ChatGPT Plus",
      monthly: 20,
      yearly: 240,
      replacement: "GPT-5.2 incluido"
    },
    {
      service: "Claude Pro",
      monthly: 20,
      yearly: 240,
      replacement: "Claude 4 incluido"
    },
    {
      service: "Jasper (contenido)",
      monthly: 49,
      yearly: 588,
      replacement: "Generación texto incluida"
    },
    {
      service: "Zapier (automatización)",
      monthly: 29,
      yearly: 348,
      replacement: "Chatbot automatizado"
    },
    {
      service: "Notion AI",
      monthly: 10,
      yearly: 120,
      replacement: "Productividad IA incluida"
    },
    {
      service: "DALL-E/Midjourney",
      monthly: 20,
      yearly: 240,
      replacement: "Generación imágenes incluida"
    }
  ];

  const totalSavings = savingsComparison.reduce((acc, item) => acc + item.yearly, 0);

  // Planes actualizados
  const plans = [
    {
      name: "Gratuito",
      price: 0,
      credits: 1000,
      features: ["1,000 créditos", "Prueba todos los modelos", "Soporte básico"],
      color: "from-gray-400 to-gray-600",
      audience: "Para probar"
    },
    {
      name: "Básico",
      price: 15.99,
      credits: 8000,
      features: ["8,000 créditos", "GPT-5.1, Claude 4", "Chatbot básico", "Soporte email"],
      color: "from-blue-400 to-blue-600",
      audience: "Individuales"
    },
    {
      name: "Estándar",
      price: 25.99,
      credits: 15000,
      features: ["15,000 créditos", "Todos los modelos", "Chatbot avanzado", "Ciberseguridad", "Soporte prioritario"],
      color: "from-green-400 to-emerald-600",
      audience: "Profesionales",
      recommended: true
    },
    {
      name: "Premium",
      price: 49.99,
      credits: 50000,
      features: ["50,000 créditos", "API completa", "Multi-agentes", "Legal + Contable", "Soporte 24/7"],
      color: "from-purple-400 to-purple-600",
      audience: "Empresas"
    },
    {
      name: "Elite",
      price: 99.99,
      credits: 120000,
      features: ["120,000 créditos", "Todo incluido", "Jarvis Master AI", "Integraciones custom", "Soporte dedicado"],
      color: "from-yellow-400 to-orange-500",
      audience: "Corporativo"
    }
  ];

  // Beneficios de referidos actualizados
  const referralTiers = [
    {
      tier: "🥉 Bronce",
      referrals: "0-5",
      perReferral: "+500 créditos",
      bonus: "🎁 +500 extra primer referido",
      perks: []
    },
    {
      tier: "🥈 Plata",
      referrals: "6-15",
      perReferral: "+750 créditos",
      bonus: "+2,000 al alcanzar",
      perks: ["Acceso Beta", "Soporte prioritario"]
    },
    {
      tier: "🥇 Oro",
      referrals: "16-30",
      perReferral: "+1,000 créditos",
      bonus: "+5,000 al alcanzar",
      perks: ["Prioridad procesamiento", "Prompts exclusivos"]
    },
    {
      tier: "💎 Platino",
      referrals: "31+",
      perReferral: "+1,500 créditos",
      bonus: "+15,000 al alcanzar",
      perks: ["VIP Tools", "Distintivo Élite", "Canal directo equipo"]
    }
  ];

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        
        <div className="py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-block mb-6 p-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl">
              <div className="bg-white dark:bg-[#0f1419] rounded-xl px-8 py-4">
                <h1 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  GPT-JDP
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  by Nexus Nova Systems Spa
                </p>
              </div>
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Infraestructura de IA Controlada para Empresas
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-6">
              No vendemos herramientas de IA. Creamos un <strong>sistema de IA que trabaja dentro de tu empresa</strong> bajo control humano total.
            </p>
            
            {/* Value Proposition Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
              {[
                { icon: "🤖", label: "IA Generativa", desc: "Multi-modelo" },
                { icon: "⚡", label: "Automatización", desc: "Workflows IA" },
                { icon: "🛡️", label: "Seguridad", desc: "Protocolos propios" },
                { icon: "🏢", label: "Empresarial", desc: "B2B-first" }
              ].map((item, i) => (
                <div key={i} className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg">
                  <span className="text-3xl block mb-2">{item.icon}</span>
                  <p className="font-bold text-gray-900 dark:text-white text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Launch Banner + QR Code */}
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl p-5 flex-1">
                <p className="text-lg font-bold mb-1">Precios de Lanzamiento</p>
                <p className="text-sm opacity-90">Plataforma operativa · Suscripción disponible</p>
              </div>
              
              {/* QR Code Display */}
              <div className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">📱 Escanea para acceder</p>
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code GPT-JDP"
                  className="w-32 h-32 mx-auto rounded-lg"
                />
                <p className="text-xs text-primary font-semibold mt-2">gpt-jdp.com</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { id: 'comparison', label: '📊 Comparativa' },
              { id: 'savings', label: '💰 Ahorro' },
              { id: 'plans', label: '📋 Planes' },
              { id: 'referrals', label: '🎁 Referidos' },
              { id: 'qr', label: '📱 Código QR' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-[#1a2530] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#283039]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-[#1a2530] rounded-2xl shadow-xl overflow-hidden">
            
            {/* Comparison Tab */}
            {activeTab === 'comparison' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                  📊 GPT-JDP vs Competencia
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                  Comparativa estratégica de valor - Por qué somos diferentes
                </p>
                
                {/* Mobile-friendly table */}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-[#283039]">
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Función</th>
                        <th className="text-center p-3 font-semibold text-primary">GPT-JDP</th>
                        <th className="text-center p-3 font-semibold text-gray-500 dark:text-gray-400">Competencia</th>
                        <th className="text-left p-3 font-semibold text-green-600">Ventaja</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketComparison.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="p-3">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">{row.feature}</p>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-green-500 text-lg">✓</span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.gptjdpDetail}</p>
                          </td>
                          <td className="p-3 text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{row.competitors}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{row.competitorDetail}</p>
                          </td>
                          <td className="p-3">
                            <span className="inline-block px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                              {row.advantage}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Differentiator Box */}
                <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-center">
                    🎯 Diferenciador Clave
                  </h4>
                  <p className="text-center text-gray-700 dark:text-gray-300 mb-4">
                    GPT-JDP une <strong>4 mercados en uno</strong>:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { num: "1", label: "IA Generativa" },
                      { num: "2", label: "Automatización" },
                      { num: "3", label: "Seguridad IA" },
                      { num: "4", label: "Operación Empresarial" }
                    ].map((item, i) => (
                      <div key={i} className="text-center p-3 bg-white dark:bg-[#1a2530] rounded-lg shadow">
                        <span className="inline-block w-8 h-8 bg-primary text-white rounded-full text-sm font-bold leading-8 mb-1">
                          {item.num}
                        </span>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{item.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
                    <strong>Ningún competidor combina los 4.</strong> Esto es un océano azul.
                  </p>
                </div>
              </div>
            )}

            {/* Savings Tab */}
            {activeTab === 'savings' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                  💰 Ahorro al Centralizar
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                  Lo que gastarías contratando cada servicio por separado
                </p>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-[#283039]">
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Servicio</th>
                        <th className="text-center p-3 font-semibold text-gray-500">Mensual</th>
                        <th className="text-center p-3 font-semibold text-red-500">Anual</th>
                        <th className="text-left p-3 font-semibold text-green-600">En GPT-JDP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savingsComparison.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="p-3 font-medium text-gray-900 dark:text-white">{row.service}</td>
                          <td className="p-3 text-center text-gray-600 dark:text-gray-400">${row.monthly}</td>
                          <td className="p-3 text-center text-red-500 font-semibold">${row.yearly}</td>
                          <td className="p-3">
                            <span className="text-green-600 text-sm">✓ {row.replacement}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-red-50 dark:bg-red-900/20">
                        <td className="p-3 font-bold text-gray-900 dark:text-white" colSpan="2">
                          Total que gastarías:
                        </td>
                        <td className="p-3 text-center text-red-600 font-black text-xl">
                          ${totalSavings}/año
                        </td>
                        <td className="p-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Savings Summary */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white text-center">
                  <p className="text-lg mb-2">Con GPT-JDP Plan Estándar</p>
                  <p className="text-4xl font-black mb-2">$25.99/mes</p>
                  <p className="text-sm opacity-90 mb-4">= $311.88/año</p>
                  <div className="bg-white/20 rounded-lg p-4">
                    <p className="text-2xl font-bold">Ahorras hasta ${totalSavings - 312}/año</p>
                    <p className="text-sm opacity-80">({Math.round(((totalSavings - 312) / totalSavings) * 100)}% de ahorro)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  📋 Planes y Precios
                </h3>
                
                <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {plans.map((plan, i) => (
                    <div 
                      key={i}
                      className={`rounded-xl p-4 border-2 ${
                        plan.recommended 
                          ? 'border-primary bg-primary/5' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {plan.recommended && (
                        <span className="block text-center text-xs text-primary font-bold mb-2">
                          ⭐ RECOMENDADO
                        </span>
                      )}
                      <div className={`h-2 rounded-full bg-gradient-to-r ${plan.color} mb-3`}></div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-center">{plan.name}</h4>
                      <p className="text-2xl font-black text-center text-gray-900 dark:text-white my-2">
                        ${plan.price}
                        {plan.price > 0 && <span className="text-sm font-normal">/mes</span>}
                      </p>
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-3">
                        {plan.credits.toLocaleString()} créditos
                      </p>
                      <ul className="space-y-1">
                        {plan.features.map((f, j) => (
                          <li key={j} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1">
                            <span className="text-green-500">✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        {plan.audience}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                  🎁 Programa de Referidos
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                  Gana créditos + beneficios exclusivos
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {referralTiers.map((tier, i) => (
                    <div key={i} className="bg-gray-50 dark:bg-[#283039] rounded-xl p-4">
                      <p className="text-2xl text-center mb-2">{tier.tier.split(' ')[0]}</p>
                      <p className="font-bold text-center text-gray-900 dark:text-white">{tier.tier.split(' ')[1]}</p>
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-3">{tier.referrals} referidos</p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Por referido:</span>
                          <span className="font-semibold text-green-600">{tier.perReferral}</span>
                        </div>
                        <div className="text-center py-1 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-700 dark:text-yellow-400">
                          {tier.bonus}
                        </div>
                      </div>
                      
                      {tier.perks.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          {tier.perks.map((perk, j) => (
                            <p key={j} className="text-xs text-primary">✓ {perk}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <Link
                    to="/register"
                    className="inline-block px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold hover:opacity-90 transition-opacity"
                  >
                    🚀 Únete Ahora y Obtén tu Código
                  </Link>
                </div>
              </div>
            )}

            {/* QR Code Tab */}
            {activeTab === 'qr' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                  📱 Código QR de GPT-JDP
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                  Descarga y comparte para marketing y presentaciones
                </p>

                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  {/* Main QR */}
                  <div className="bg-white dark:bg-[#283039] rounded-2xl p-8 text-center shadow-lg">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-4">🔍 Info Completa de Plataforma</h4>
                    <div className="bg-white p-4 rounded-xl inline-block mb-4">
                      <img 
                        src={qrCodeUrl}
                        alt="QR Code GPT-JDP Info"
                        className="w-48 h-48"
                      />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Escanea para ver toda la información de la plataforma
                    </p>
                    <a
                      href={qrCodeUrl}
                      download="gpt-jdp-info-qr.png"
                      className="inline-block px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90"
                    >
                      📥 Descargar QR
                    </a>
                  </div>

                  {/* Additional QRs */}
                  <div className="space-y-4">
                    {[
                      { 
                        label: "📝 Página de Registro", 
                        url: "https://gpt-jdp.com/register",
                        desc: "Directo al formulario de registro"
                      },
                      { 
                        label: "💎 Página de Planes", 
                        url: "https://gpt-jdp.com/pricing",
                        desc: "Ver todos los planes y precios"
                      },
                      { 
                        label: "🏠 Página Principal", 
                        url: "https://gpt-jdp.com",
                        desc: "Página de inicio"
                      }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 bg-gray-50 dark:bg-[#283039] p-4 rounded-xl">
                        <div className="bg-white p-2 rounded-lg">
                          <img 
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(item.url)}`}
                            alt={item.label}
                            className="w-16 h-16"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 dark:text-white text-sm">{item.label}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                        </div>
                        <a
                          href={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(item.url)}`}
                          download={`gpt-jdp-${item.label.split(' ')[1].toLowerCase()}-qr.png`}
                          className="text-primary hover:text-primary/80 text-sm font-medium"
                        >
                          📥
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-8 max-w-2xl mx-auto bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
                  <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-3">
                    💡 Recomendaciones de Uso
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-2">
                    <li>• <strong>Info Completa:</strong> Marketing general, presentaciones, material impreso</li>
                    <li>• <strong>Registro:</strong> Campañas de captación, eventos, redes sociales</li>
                    <li>• <strong>Planes:</strong> Ventas directas, promociones, cotizaciones</li>
                    <li>• <strong>Principal:</strong> Tarjetas de presentación, firmas de email</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Final CTA */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-3">
                ¿Listo para transformar tu empresa?
              </h3>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                Deja de pagar múltiples suscripciones. Centraliza toda tu IA en una sola plataforma con control total.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="px-8 py-3 bg-white text-primary rounded-xl font-bold hover:bg-gray-100 transition-colors"
                >
                  Crear Cuenta Gratis
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-3 bg-white/20 text-white border-2 border-white rounded-xl font-bold hover:bg-white/30 transition-colors"
                >
                  Ver Planes
                </Link>
              </div>
            </div>

            {/* Company Info */}
            <div className="mt-8 text-gray-500 dark:text-gray-400 text-sm">
              <p>© 2025-2026 Nexus Nova Systems Spa (Chile)</p>
              <p className="mt-1">contact@gpt-jdp.com | gpt-jdp.com</p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default QRInfo;
